import { useState } from 'react';
import { useGameStore } from '../state/useGameStore';
import { SettingsPanel } from './SettingsPanel';
import { HelpOverlay } from './HelpOverlay';
import { disconnectGameServer, sendInput } from '../game/network';
import { getCameraSettings, setCameraSettings, type CameraSettings } from '../game/cameraSettings';
import { getAudioCue, playAudioCue } from '../game/audio';
import { requestPointerLockSafe } from '../game/pointerLock';

const pauseKeyframes = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideInFromTop {
    from { opacity: 0; transform: translateY(-30px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

function PauseButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => {
        const cue = getAudioCue('ui-confirm');
        if (cue) playAudioCue(cue);
        onClick();
      }}
      onMouseEnter={() => {
        const cue = getAudioCue('ui-hover');
        if (cue) playAudioCue(cue);
      }}
      onPointerEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 0 24px #62ddff60, 0 0 48px #62ddff20';
        e.currentTarget.style.background = 'rgba(98,221,255,0.12)';
        e.currentTarget.style.transform = 'scale(1.02)';
      }}
      onPointerLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.background = 'rgba(98,221,255,0.06)';
        e.currentTarget.style.transform = 'scale(1)';
      }}
      style={{
        minWidth: 240,
        padding: '14px 28px',
        fontSize: 14,
        letterSpacing: 3,
        textTransform: 'uppercase',
        fontFamily: 'system-ui, sans-serif',
        fontWeight: 700,
        color: '#62ddff',
        background: 'rgba(98,221,255,0.06)',
        border: '1px solid rgba(98,221,255,0.35)',
        cursor: 'pointer',
        transition: 'box-shadow 0.25s ease, background 0.25s ease, transform 0.25s ease',
      }}
    >
      {label}
    </button>
  );
}

export function PauseMenu() {
  const [showSettings, setShowSettings] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [cameraSettings, setLocalCameraSettings] = useState<CameraSettings>(() => getCameraSettings());

  const handleSettingsChange = (next: CameraSettings) => {
    setCameraSettings(next);
    setLocalCameraSettings(next);
  };

  const handleResume = () => {
    useGameStore.getState().setGamePhase('playing');
    requestPointerLockSafe(document.body);
  };

  const handleExit = () => {
    disconnectGameServer();
    document.exitPointerLock?.();
    useGameStore.getState().setGamePhase('menu');
  };

  const handleSwitchTeam = () => {
    const state = useGameStore.getState();
    sendInput({
      moveX: 0,
      moveY: 0,
      jump: false,
      jet: false,
      yaw: state.localPlayer.yaw ?? 0,
      pitch: state.localPlayer.pitch ?? 0,
      fire: false,
      tick: 0,
      teamSwitch: true,
    });
    state.setGamePhase('playing');
    requestPointerLockSafe(document.body);
  };

  return (
    <div
      role="dialog"
      aria-label="Pause menu"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(4,8,14,0.72)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        zIndex: 130,
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      <style>{pauseKeyframes}</style>
      <div
        style={{
          width: 'min(420px, 90vw)',
          padding: 28,
          background: 'linear-gradient(180deg, rgba(7, 12, 20, 0.97), rgba(4, 8, 14, 0.95))',
          border: '1px solid rgba(155, 185, 212, 0.25)',
          boxShadow: '0 18px 50px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          animation: 'slideInFromTop 0.35s ease-out',
        }}
      >
        <div
          style={{
            fontSize: 18,
            letterSpacing: 4,
            textTransform: 'uppercase',
            fontWeight: 800,
            color: '#62ddff',
            marginBottom: 8,
          }}
        >
          PAUSED
        </div>

        <PauseButton label="RESUME" onClick={handleResume} />
        <PauseButton label="SWITCH TEAM" onClick={handleSwitchTeam} />
        <PauseButton label="SETTINGS" onClick={() => setShowSettings((prev) => !prev)} />
        <PauseButton label="CONTROLS" onClick={() => setShowControls((prev) => !prev)} />
        <PauseButton label="EXIT TO MENU" onClick={handleExit} />

        {showSettings && (
          <div style={{ marginTop: 8, width: '100%' }}>
            <SettingsPanel
              visible
              settings={cameraSettings}
              onSettingsChange={handleSettingsChange}
              onClose={() => setShowSettings(false)}
            />
          </div>
        )}

        {showControls && (
          <div style={{ marginTop: 8, width: '100%' }}>
            <HelpOverlay connected={useGameStore.getState().connected} compact minimal />
          </div>
        )}
      </div>
    </div>
  );
}
